// Spec iter122 (Feb 2026) — Homogénéisation des badges de statut.
// Nouveau style unique : icône colorée + texte de la même couleur, sans
// bordure ni fond, font-weight 600, texte 13 px, icône 15 px.
//
// (Rappel du bug 2026-02 : `Lock` bare peut collisionner avec le global
// navigateur `window.Lock` (Web Locks API) après minification. Alias
// systématique `Lock as LockIcon`.)
import { Ban, Clock, Eye, Pause, AlertTriangle, Lock as LockIcon, CircleDot, X } from "lucide-react";

/**
 * DocumentStatusBadge — 6 statuts métier (spec iter122 + iter242).
 *
 *   • En cours       — icône Clock,           #F9A826
 *   • À vérifier     — icône Eye,             #c9b8f5   (attribué à `termine`
 *                                              ET `terminee` — l'ancienne
 *                                              distinction n'est plus exposée
 *                                              côté UI, cf. spec.)
 *   • Clôturé        — icône Lock,            #8B5CF6
 *   • Suspendu       — icône Pause,           #5aa8e0
 *   • Délai dépassé  — icône AlertTriangle,   #e07a7a
 *   • Annulé         — icône Ban,             #9CA3AF   (spec iter242 —
 *                                              statut FINAL, gris pour
 *                                              signaler l'inactivité)
 */
export const DOC_STATUS_MAP = {
  en_cours:      { label: "En cours",      color: "#F9A826", Icon: Clock },
  // Alias `terminee` (auto-closed pending review) → même style que `termine`
  // pour ne conserver que 5 statuts visibles côté UI.
  terminee:      { label: "À vérifier",    color: "#c9b8f5", Icon: Eye },
  termine:       { label: "À vérifier",    color: "#c9b8f5", Icon: Eye },
  cloture:       { label: "Clôturé",       color: "#8B5CF6", Icon: LockIcon },
  suspendu:      { label: "Suspendu",      color: "#5aa8e0", Icon: Pause },
  delai_depasse: { label: "Délai dépassé", color: "#e07a7a", Icon: AlertTriangle },
  annule:        { label: "Annulé",        color: "#9CA3AF", Icon: Ban },
  ouvert:        { label: "Ouvert",        color: "#e6c383", Icon: CircleDot },   // iter144 — mode Dossier
  a_corriger:    { label: "À corriger",    color: "#F9A826", Icon: AlertTriangle }, // iter150 — Dossier repassé en correction
};

export const DOC_STATUS_ORDER = ["en_cours", "delai_depasse", "termine", "cloture", "suspendu", "annule"];

/**
 * Formatage court d'un délai de retard en jours ouvrés (dim-jeu, 8h-17h Alger).
 * ``business_seconds`` provient du backend (``_decorate`` → ``overdue_business_seconds``).
 * 32400 secondes = 9h de travail par jour, donc 1 jour ouvré = 32400 s.
 */
export function formatOverdueDays(business_seconds) {
  if (!business_seconds || business_seconds <= 0) return "à l'instant";
  const days = business_seconds / 32400;
  if (days < 1) {
    const hours = Math.max(1, Math.round(business_seconds / 3600));
    return `${hours}h`;
  }
  return `${Math.floor(days)}j`;
}

const fmtTipDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Africa/Algiers" }) : "");

/** iter383 pt 1 — infobulle du statut Clôturé : « Clôturé · annulé par <nom> le <date> » ou « Clôturé par <nom> le <date> ». */
export function closureTooltip({ cancelled, actorName, at }) {
  const who = [actorName && `par ${actorName}`, at && `le ${fmtTipDate(at)}`].filter(Boolean).join(" ");
  return cancelled ? `Clôturé · annulé${who ? ` ${who}` : ""}` : `Clôturé${who ? ` ${who}` : ""}`;
}

/**
 * iter383 pt 1 — un document annulé est aussi clôturé : modèle « Clôturé » (cadenas violet + libellé) + petit ✕ rouge
 * en bas à droite du cadenas (rond 12 px #E24B4A, ✕ blanc 9 px, contour 2 px `ring` = fond de la ligne, right −6 / bottom −5).
 * `actorName` / `at` alimentent l'infobulle (annulé par … le … / clôturé par … le …).
 */
export default function DocumentStatusBadge({ status: rawStatus, overdue_business_seconds, cancelled = false, actorName = "", at = null, ring = "#141418" }) {
  cancelled = cancelled || rawStatus === "annule";
  const status = cancelled ? "cloture" : rawStatus;
  const s = DOC_STATUS_MAP[status] || DOC_STATUS_MAP.en_cours;
  const Ico = s.Icon;
  const label = s.label;
  const tooltipTitle = status === "cloture"
    ? closureTooltip({ cancelled, actorName, at })
    : status === "delai_depasse" && overdue_business_seconds
      ? `${s.label} · ${formatOverdueDays(overdue_business_seconds)}`
      : label;
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap"
      style={{
        color: s.color,
        fontWeight: 600,
        fontSize: 13,
        lineHeight: 1.1,
        maxWidth: "100%",
        minWidth: 0,
      }}
      title={tooltipTitle}
      data-testid={`doc-status-${status}`}
      data-cancelled={cancelled || undefined}
    >
      <span className="relative inline-flex shrink-0">
        <Ico size={15} strokeWidth={2.2} className="shrink-0" />
        {cancelled && (
          <span className="absolute inline-flex items-center justify-center" aria-label="annulé"
            style={{ right: -6, bottom: -5, width: 12, height: 12, borderRadius: "50%", background: "#E24B4A", boxShadow: `0 0 0 2px ${ring}` }} data-testid="doc-status-cancelled-dot">
            <X size={9} strokeWidth={3} color="#fff" />
          </span>
        )}
      </span>
      <span className="truncate">{label}</span>
    </span>
  );
}
